import { useState } from "react";
import { useSearchParams } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type Tool, type ToolCategory, TOOLS } from "@/data/tools";
import { cn } from "@/lib/utils";

type CategoryFilter = "All" | ToolCategory;

const CATEGORIES: CategoryFilter[] = [
  "All",
  "Scoping",
  "Ideation",
  "Validation",
  "Business Model",
  "Pitching",
];

function ToolCard({ tool }: { tool: Tool }) {
  return (
    <div className="border-border flex flex-col rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex-1">
        <p className="text-foreground font-semibold">{tool.title}</p>
        <p className="text-muted mt-2 text-sm leading-relaxed">{tool.description}</p>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        {/* Module tags + print size */}
        <div className="flex flex-wrap gap-1.5">
          {tool.modules.map((m) => (
            <span
              key={m}
              className="bg-accent-light text-accent rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              M{m}
            </span>
          ))}
          <span className="border-border text-muted rounded-full border px-2.5 py-0.5 text-xs font-medium">
            {tool.printSize}
          </span>
        </div>

        {/* Download button */}
        <div className="relative flex-shrink-0">
          {tool.downloadUrl ? (
            <a href={tool.downloadUrl} download>
              <Button size="sm" variant="outline">
                Download
              </Button>
            </a>
          ) : (
            <div className="group relative">
              <Button size="sm" variant="outline" disabled>
                Download
              </Button>
              <div className="bg-foreground pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity group-hover:opacity-100">
                Coming soon
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ToolRepositoryPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("All");

  const filtered = TOOLS.filter((tool) => {
    const matchesSearch =
      search.trim() === "" ||
      tool.title.toLowerCase().includes(search.toLowerCase()) ||
      tool.description.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = activeCategory === "All" || tool.category === activeCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <p className="text-primary mb-2 text-xs font-semibold tracking-[0.24em] uppercase">
            Tesla Program
          </p>
          <h1 className="font-heading text-foreground text-2xl font-bold">Tool Repository</h1>
          <p className="text-muted mt-2 text-sm">
            Download templates and canvases for every stage of your innovation journey.
          </p>
        </div>

        {/* Search + filters */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <svg
              className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <Input
              placeholder="Search tools…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                  activeCategory === cat
                    ? "bg-primary border-primary text-white"
                    : "border-border text-muted hover:border-primary/40 hover:text-foreground bg-white",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className="text-muted mb-4 text-sm">
          {filtered.length} tool{filtered.length !== 1 ? "s" : ""}
        </p>

        {/* Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[30vh] flex-col items-center justify-center text-center">
            <p className="text-foreground font-semibold">No tools found</p>
            <p className="text-muted mt-2 text-sm">
              Try adjusting your search or selecting a different category.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setActiveCategory("All");
              }}
              className="text-primary mt-3 text-sm hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
